<template>
  <div
    class="tw-relative"
    v-bind:class="{ hovereffect: editable, 'tw-rounded-full': rounded }"
    :style="{ height: height ? height + 'px' : '100%', width: width ? width + 'px' : '100%' }"
  >
    <img
      v-if="hasAvatar"
      v-bind:class="{ 'tw-rounded-full': rounded }"
      class="tw-block tw-object-cover"
      :src="imgSrc"
      :style="{ height: height ? height + 'px' : '100%', width: width ? width + 'px' : '100%' }"
      @error="onAvatarError"
    />
    <img
      v-if="!hasAvatar && avatarType === 'user'"
      class="tw-border tw-object-cover"
      :class="{ 'tw-rounded-full': rounded }"
      :height="height ? height + 'px' : '100%'"
      :width="width ? width + 'px' : '100%'"
      src="@/assets/images/avatar-blank-user.png"
    />
    <img
      v-if="!hasAvatar && avatarType === 'business'"
      class="tw-border tw-object-cover"
      :class="{ 'tw-rounded-full': rounded }"
      :height="height ? height + 'px' : '100%'"
      :width="width ? width + 'px' : '100%'"
      src="@/assets/images/avatar-blank-business.png"
    />
    <img
      v-if="!hasAvatar && avatarType === 'plan'"
      class="tw-border tw-object-cover tw-p-1 tw-mx-auto tw-my-auto"
      :class="{ 'tw-rounded-full': rounded }"
      :height="height ? height + 'px' : '100%'"
      :width="width ? width + 'px' : '100%'"
      src="@/assets/images/avatar-blank-business-plan.png"
    />
    <img
      v-if="!hasAvatar && avatarType === 'integration'"
      class="tw-border tw-object-cover"
      :class="{ 'tw-rounded-full': rounded }"
      :height="height ? height + 'px' : '100%'"
      :width="width ? width + 'px' : '100%'"
      src="@/assets/images/avatar-blank-integration.png"
    />
    <img
      v-if="!hasAvatar && avatarType === 'training'"
      class="tw-border tw-object-cover"
      :class="{ 'tw-rounded-full': rounded }"
      :height="height ? height + 'px' : '100%'"
      :width="width ? width + 'px' : '100%'"
      src="@/assets/images/avatar-blank-training.png"
    />
    <div
      v-if="editable"
      class="tw-opacity-0 hover:tw-opacity-100 tw-bg-gray-400 tw-duration-300 tw-left-0 tw-top-0 tw-h-full tw-w-full tw-absolute tw-flex tw-text-xs tw-text-white tw-font-light"
      @click="show"
      :class="{ 'tw-rounded-full': rounded }"
    >
      <div class="tw-flex tw-flex-col tw-w-full tw-justify-center tw-items-center">
        <span class="tw-text-center">Upload</span>
        <upload-icon class="tw-h-4 tw-w-4 tw-mt-1 tw-text-white" />
      </div>
    </div>
    <base-modal v-if="editable" ref="avatarModal" size="sm">
      <base-card shadow>
        <div class="tw-flex tw-w-full tw-justify-center">
          <image-input class="tw-mt-6" v-model="imageFile" :key="imageInputKey" />
        </div>
        <div class="sm:tw-flex sm:tw-flex-row-reverse tw-mt-6">
          <base-button @click="upload" :disabled="isLoading" :loading="isLoading" class="tw-w-full sm:tw-w-auto sm:tw-ml-3">Upload</base-button>
          <base-button @click="hide" type="flat" class="tw-w-full sm:tw-w-auto tw-mt-2 sm:tw-mt-0">
            Cancel
          </base-button>
        </div>
      </base-card>
    </base-modal>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import ImageInput from "@/components/ImageInput";
import { UploadIcon } from "@/assets/svgs";

export default {
  name: "Avatar",
  components: {
    ImageInput,
    UploadIcon,
  },
  props: {
    height: Number,
    width: Number,
    userId: {
      type: String,
      default: "",
    },
    businessId: {
      type: String,
      default: "",
    },
    planId: {
      type: String,
      default: "",
    },
    integrationId: {
      type: String,
      default: "",
    },
    trainingId: {
      type: String,
      default: "",
    },
    avatarType: {
      type: String,
      validator(value) {
        return ["user", "business", "plan", "integration", "training"].indexOf(value) !== -1;
      },
    },
    editable: {
      type: Boolean,
      default: false,
    },
    rounded: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      isLoading: false,
      avatarError: false,
      imageInputKey: 0,
      imageFile: null,
    };
  },
  computed: {
    ...mapGetters("avatar", ["getVersion"]),
    version() {
      return this.getVersion(this.id);
    },
    id() {
      return this.userId + this.businessId + this.planId + this.integrationId + this.trainingId;
    },
    hasAvatar() {
      return (this.userId || this.businessId || this.planId || this.integrationId || this.trainingId) && !this.avatarError;
    },
    imgSrc() {
      switch (this.avatarType) {
        case "user":
          return `/api/uploads/users/${this.userId}/avatar/base-image?v=${this.version}`;
        case "business":
          return `/api/uploads/businesses/${this.businessId}/avatar/base-image?v=${this.version}`;
        case "plan":
          return `/api/uploads/businesses/${this.businessId}/plans/${this.planId}/avatar/base-image?v=${this.version}`;
        case "integration":
          return `/api/uploads/integrations/${this.integrationId}/avatar/base-image?v=${this.version}`;
        case "training":
          return `/api/uploads/trainings/${this.trainingId}/avatar/base-image?v=${this.version}`;
        default:
          return "";
      }
    },
    imgUpload() {
      switch (this.avatarType) {
        case "user":
          return `/users/${this.userId}/upload/avatar`;
        case "business":
          return `/businesses/${this.businessId}/upload/avatar`;
        case "integration":
          return `/integrations/${this.integrationId}/avatar`;
        case "training":
          return `/businesses/${this.businessId}/trainings/${this.trainingId}/avatar`;
        case "plan":
          return `/businesses/${this.businessId}/plans/${this.planId}/upload/avatar`;
        default:
          return "";
      }
    },
  },
  methods: {
    ...mapActions("avatar", ["updateVersion"]),
    ...mapActions("businessActivation", { businessActivationInit: "init" }),
    reset() {
      this.imageInputKey++;
      this.isLoading = false;
      this.imageFile = null;
    },
    onAvatarError() {
      this.avatarError = true;
    },
    show() {
      this.reset();
      this.$refs.avatarModal.show();
    },
    hide() {
      this.$refs.avatarModal.hide();
    },
    upload() {
      this.isLoading = true;

      if (!this.imageFile) {
        this.isLoading = false;
        return;
      }

      var contentType = this.imageFile.type;

      this.$axios
        .post(this.imgUpload, { ContentType: contentType })
        .then((res) => {
          this.$axios
            .put(res.data, this.imageFile, { headers: { "Content-Type": contentType } })
            .then(() => {
              this.hide();
              this.updateVersion(this.id);
              this.avatarError = false;
              this.isLoading = false;
              this.EventBus.$emit("GLOBAL_NOTIFY", { title: "Image Uploaded", message: "Your image was uploaded successfully" });
              if (this.avatarType === "business") this.businessActivationInit();
              this.$emit("image-uploaded");
            })
            .catch((err) => {
              this.isLoading = false;
              this.errors = err.response.data;
            });
        })
        .catch((err) => {
          this.isLoading = false;
          this.errors = err.response.data;
        });
    },
  },
  watch: {
    hasAvatar: {
      immediate: true,
      handler() {
        this.$emit("update:hasAvatar", this.hasAvatar);
      },
    },
  },
};
</script>
